import { useCallback, useMemo } from 'react';

import { cx } from '@libs/classnames';
import { formatName } from '@libs/formatNames';
import useLockBodyScroll from '@libs/hooks/useLockBodyScroll';
import { Card } from '@ui/components/Card';
import { InfiniteScroll } from '@ui/components/InfiniteScroll';
import { PageSubTitle } from '@ui/components/PageSubTitle';
import { Popover } from '@ui/components/Popover';
import { SearchInput } from '@ui/components/SearchInput';
import { Table, TableCell, TableRow } from '@ui/components/Table';
import { Tab, Tabs } from '@ui/components/Tabs';
import { ThemeButton } from '@ui/components/ThemeButton';

import { MemberTournamentParticipantDto, Tournament } from '@features/shared/api/typings';
import { DEFAULT_MAX_TOURNAMENT_PARTICIPANTS } from '@features/tournament/constants';
import { useTournamentStates } from '@features/tournament/hooks/useTournamentStates';

import styles from './ParticipantsModal.module.css';

type Sort = 'AlphabetAsc' | 'RatingDesc';

const SORT_TABS: Tab<Sort>[] = [
  { value: 'RatingDesc', label: 'По рейтингу' },
  { value: 'AlphabetAsc', label: 'По алфавиту' },
];

export type Props = {
  className?: string;
  isOpen: boolean;
  hide: () => void;
  tournament: Tournament;
  sort: Sort;
  onSortChange: (sort: Sort) => void;
  search?: string;
  onSearch: (search: string) => void;
  members?: MemberTournamentParticipantDto[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  onAdd?: (p: MemberTournamentParticipantDto) => void;
  onInvite?: (p: MemberTournamentParticipantDto) => void;
  onDelete?: (p: MemberTournamentParticipantDto) => void;
  groupTitle?: string | null;
};

export function ParticipantsModal({
  className,
  isOpen,
  hide,
  tournament,
  sort,
  onSortChange,
  search,
  onSearch,
  members,
  hasNextPage,
  fetchNextPage,
  onAdd,
  onInvite,
  onDelete,
  groupTitle,
}: Props) {
  useLockBodyScroll(isOpen);

  const participantsCount = useMemo(
    () =>
      members?.filter((m) => m.participationStatus === 'Participating').length ||
      tournament.participantsCount,
    [members, tournament.participantsCount],
  );

  const { participantsCountMax } = tournament;

  const { isEditable } = useTournamentStates(tournament);

  const isMaxLimit =
    participantsCount >= (participantsCountMax || DEFAULT_MAX_TOURNAMENT_PARTICIPANTS);

  return (
    <div
      className={cx(styles.participantsPopoverWrapper, {
        [styles.active]: isOpen,
      })}
    >
      <Popover className={styles.participantsPopover} active={isOpen} onHide={hide}>
        <Card className={cx(className, styles.participantsModal)}>
          <div className={styles.header}>
            <div className={styles.title}>
              <PageSubTitle>Участники</PageSubTitle>
              <Tabs tabs={SORT_TABS} onChange={onSortChange} value={sort} disabled={!isEditable} />
              <SearchInput
                onQueryChange={onSearch}
                placeholder="Поиск участников"
                query={search}
                disabled={!isEditable}
              />
            </div>
            <ThemeButton className={styles.saveButton} onClick={hide}>
              Готово
            </ThemeButton>
          </div>

          <InfiniteScroll
            scrollableClassName={styles.scrollContainer}
            fetchMore={fetchNextPage}
            hasMore={hasNextPage}
            itemsLength={members?.length || 0}
          >
            <Table className={styles.participantsTable}>
              <TableRow header>
                <TableCell className={styles.name}>Имя</TableCell>
                <TableCell></TableCell>
                <TableCell>Email</TableCell>
                <TableCell>{groupTitle || 'Группа'}</TableCell>
                <TableCell right>Рейтинг</TableCell>
                <TableCell right></TableCell>
              </TableRow>

              {members?.map((p) => (
                <ParticipantRow
                  key={p.userId}
                  participant={p}
                  onAdd={onAdd}
                  onInvite={onInvite}
                  onDelete={onDelete}
                  disabled={!isEditable}
                  isMaxLimit={isMaxLimit}
                />
              ))}
            </Table>
          </InfiniteScroll>
        </Card>
      </Popover>
    </div>
  );
}

type ParticipantRowProps = {
  participant: MemberTournamentParticipantDto;
  onAdd?: (p: MemberTournamentParticipantDto) => void;
  onInvite?: (p: MemberTournamentParticipantDto) => void;
  onDelete?: (p: MemberTournamentParticipantDto) => void;
  disabled?: boolean;
  isMaxLimit: boolean;
};

function ParticipantRow({
  participant: p,
  onAdd,
  onInvite,
  onDelete,
  disabled,
  isMaxLimit,
}: ParticipantRowProps) {
  const isInvited = p.participationStatus === 'Invited';
  const isAdded = p.participationStatus === 'Participating';

  const handleAdd = useCallback(() => {
    onAdd?.(p);
  }, [onAdd, p]);
  const handleInvite = useCallback(() => {
    onInvite?.(p);
  }, [onInvite, p]);
  const handleDelete = useCallback(() => {
    onDelete?.(p);
  }, [onDelete, p]);

  return (
    <TableRow key={p.userId} hoverable>
      <TableCell className={cx(styles.breakWord, styles.padRight)}>{formatName(p)}</TableCell>
      <TableCell top className={styles.padRight}>
        {p.title}
      </TableCell>
      <TableCell className={cx(styles.breakWord, styles.padRight)}>{p.email}</TableCell>
      <TableCell className={cx(styles.breakWord, styles.padRight)}>{p.groupName}</TableCell>
      <TableCell right>{p.effectiveRating}</TableCell>
      <TableCell right>
        <div className={styles.buttons}>
          {(isAdded || isInvited) && (
            <ThemeButton
              className={styles.addButton}
              compact
              inverted
              onClick={handleDelete}
              disabled={disabled || !onDelete}
            >
              {isInvited && onDelete && 'Отменить'}
              {isAdded && onDelete && 'Удалить'}
              {isAdded && !onDelete && 'Добавлен'}
            </ThemeButton>
          )}

          {onAdd && !isAdded && (
            <ThemeButton
              className={styles.addButton}
              compact
              onClick={handleAdd}
              disabled={isMaxLimit || disabled}
            >
              Добавить
            </ThemeButton>
          )}

          {onInvite && !isInvited && (
            <ThemeButton
              className={styles.addButton}
              compact
              onClick={handleInvite}
              disabled={isMaxLimit || disabled}
            >
              Пригласить
            </ThemeButton>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
